import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Plus, Trash2, ChevronDown, ChevronUp, Save, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-bronze outline-none px-4 py-3 text-sm text-bone font-light transition-colors duration-300";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-muted2 block mb-2";

export const Field = ({ label, value, onChange, textarea = false, testid }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea data-testid={testid} rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} />
    ) : (
      <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    )}
  </div>
);

export const ImageField = ({ label, value, onChange, token, testid }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch {
      toast.error("Upload failed — please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      {label && <label className={labelCls}>{label}</label>}
      <div className="flex items-start gap-4">
        {value ? (
          <img src={value} alt="" className="w-28 h-20 object-cover border border-white/10 shrink-0" />
        ) : (
          <div className="w-28 h-20 border border-dashed border-white/15 shrink-0 flex items-center justify-center text-[9px] uppercase tracking-widest text-muted2">
            No image
          </div>
        )}
        <div className="flex-1 space-y-2">
          <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="/images/... or upload a file" className={inputCls} />
          <label className="inline-flex items-center gap-2 cursor-pointer text-[10px] uppercase tracking-[0.25em] text-bronze border border-bronze/40 px-4 py-2 transition-all duration-300 hover:bg-bronze hover:text-obsidian">
            <Upload size={12} /> {busy ? "Uploading…" : "Upload Image"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} />
          </label>
        </div>
      </div>
    </div>
  );
};

export const Section = ({ title, desc, saving, onSave, onReset, children, testid }) => (
  <div data-testid={testid} className="border border-white/[0.07] bg-ink/30 p-6 sm:p-8 space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="font-serif text-xl font-light text-bone">{title}</h3>
        {desc && <p className="text-xs text-muted2 mt-1 font-light">{desc}</p>}
      </div>
      <div className="flex items-center gap-2">
        {onReset && (
          <button type="button" onClick={onReset} data-testid={`${testid}-reset-btn`} className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted2 border border-white/15 px-4 py-2.5 transition-colors duration-300 hover:border-bronze hover:text-bronze">
            <RotateCcw size={12} /> Reset
          </button>
        )}
        <button type="button" onClick={onSave} data-testid={`${testid}-save-btn`} className="inline-flex items-center gap-2 bg-bronze text-obsidian text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 transition-colors duration-300 hover:bg-bronze-light disabled:opacity-50" disabled={saving}>
          <Save size={12} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
    {children}
  </div>
);

export const LineArrayField = ({ label, value, onChange, testid }) => (
  <Field
    label={label}
    textarea
    testid={testid}
    value={(value || []).join("\n")}
    onChange={(v) => onChange(v.split("\n").map((s) => s.trim()).filter(Boolean))}
  />
);

export const ListEditor = ({ items, onChange, makeNew, addLabel, itemTitle, renderItem, testid }) => {
  const [open, setOpen] = useState(null);
  const update = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((item, i) => (
        <div key={i} className="border border-white/10">
          <div className="w-full flex items-center justify-between px-5 py-4">
            <button type="button" onClick={() => setOpen(open === i ? null : i)} className="flex-1 text-left text-sm text-bone font-light hover:text-bronze transition-colors duration-300" data-testid={`${testid}-item-${i}`}>
              {itemTitle(item, i)}
            </button>
            <span className="flex items-center gap-4">
              <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-muted2 hover:text-red-400 transition-colors duration-300" aria-label="Delete item" data-testid={`${testid}-delete-${i}`}>
                <Trash2 size={14} />
              </button>
              <button type="button" onClick={() => setOpen(open === i ? null : i)} className="text-muted2" aria-label="Expand">
                {open === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </span>
          </div>
          {open === i && <div className="px-5 pb-6 pt-5 border-t border-white/10">{renderItem(item, (patch) => update(i, patch))}</div>}
        </div>
      ))}
      <button
        type="button"
        data-testid={`${testid}-add-btn`}
        onClick={() => {
          onChange([...items, makeNew()]);
          setOpen(items.length);
        }}
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-bronze border border-bronze/40 px-5 py-3 transition-all duration-300 hover:bg-bronze hover:text-obsidian"
      >
        <Plus size={13} /> {addLabel}
      </button>
    </div>
  );
};
